// /src/pages/private/members/calendarTools/CalendarSignupIntroPopup.jsx

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * First-visit helper for users who have never signed up to an event.
 * Reappears on each /members/calendar visit until EventSignup exists.
 * Uses the view-as subject when X-View-As-UserId is set (axios interceptor).
 */
export default function CalendarSignupIntroPopup({
  enabled = true,
  suppress = false,
  /** View-as target user id, or null when viewing as self — forces refetch on switch. */
  subjectUserId = null,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoPulse, setDemoPulse] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      setDemoPulse(false);
      return undefined;
    }

    let cancelled = false;
    setOpen(false);
    setDemoPulse(false);
    setLoading(true);

    const load = async () => {
      try {
        const { data } = await axios.get('/api/user/has-event-signup', {
          withCredentials: true,
        });
        if (cancelled) return;
        if (data?.ok && data.hasSignedUp !== true) {
          setOpen(true);
        } else {
          setOpen(false);
        }
      } catch (err) {
        console.error('[CalendarSignupIntroPopup] status failed', err);
        if (!cancelled) setOpen(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, subjectUserId]);

  const dismiss = useCallback(() => {
    setOpen(false);
    setDemoPulse(false);
  }, []);

  const onDemoSignupClick = useCallback(() => {
    setDemoPulse(true);
  }, []);

  if (!enabled || loading || !open || suppress) return null;

  return createPortal(
    <div
      className="calendar-signup-intro-overlay"
      role="presentation"
      data-resource="private.navbar.calendar"
      data-action="read"
      data-route="/api/user/has-event-signup"
    >
      <div
        className="calendar-signup-intro"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-signup-intro-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="calendar-signup-intro__header">
          <p className="calendar-signup-intro__eyebrow">Welcome</p>
          <h2 id="calendar-signup-intro-title" className="calendar-signup-intro__title">
            How to join an event
          </h2>
          <p className="calendar-signup-intro__lead">
            You have not signed up for an event yet. Follow these steps — it only takes a minute.
          </p>
        </header>

        <ol className="calendar-signup-intro__steps">
          <li className="calendar-signup-intro__step">
            <span className="calendar-signup-intro__step-index" aria-hidden="true">
              1
            </span>
            <div className="calendar-signup-intro__step-body">
              <h3 className="calendar-signup-intro__step-title">Open an available event</h3>
              <p className="calendar-signup-intro__step-text">
                On the calendar, click any upcoming event card to open its details page.
              </p>
            </div>
          </li>
          <li className="calendar-signup-intro__step">
            <span className="calendar-signup-intro__step-index" aria-hidden="true">
              2
            </span>
            <div className="calendar-signup-intro__step-body">
              <h3 className="calendar-signup-intro__step-title">Read the briefing</h3>
              <p className="calendar-signup-intro__step-text">
                Check the objectives, schedule, location, and any special requirements before you
                commit.
              </p>
            </div>
          </li>
          <li className="calendar-signup-intro__step">
            <span className="calendar-signup-intro__step-index" aria-hidden="true">
              3
            </span>
            <div className="calendar-signup-intro__step-body">
              <h3 className="calendar-signup-intro__step-title">Sign up at the bottom</h3>
              <p className="calendar-signup-intro__step-text">
                Scroll to the footer of the event page and press the green button — it looks like
                this:
              </p>
              <div
                className={`calendar-signup-intro__demo${
                  demoPulse ? ' calendar-signup-intro__demo--pulse' : ''
                }`}
              >
                <span className="calendar-signup-intro__demo-badge">Example</span>
                <div className="calendar-signup-intro__demo-footer footer calendar-event">
                  <button
                    type="button"
                    className="btn-style1 green calendar-signup-intro__demo-btn"
                    onClick={onDemoSignupClick}
                    data-resource="private.navbar.calendar"
                    data-action="read"
                    aria-label="Example signup button — not a real signup"
                  >
                    I Sign up
                  </button>
                </div>
                {demoPulse ? (
                  <p className="calendar-signup-intro__demo-hint" role="status">
                    Perfect — look for this exact button at the bottom of a real event page.
                  </p>
                ) : (
                  <p className="calendar-signup-intro__demo-hint calendar-signup-intro__demo-hint--muted">
                    Try the button — it is only a preview.
                  </p>
                )}
              </div>
            </div>
          </li>
        </ol>

        <footer className="calendar-signup-intro__footer">
          <button
            type="button"
            className="calendar-signup-intro__cta"
            onClick={dismiss}
            data-resource="private.navbar.calendar"
            data-action="read"
          >
            Got it — show the calendar
          </button>
          <p className="calendar-signup-intro__footnote">
            This tip will appear again until you sign up for your first event.
          </p>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
